import React, { lazy, Suspense } from 'react';
import { useVehiculos, getVehiculosNoMayoreados } from '../../services/apiService';
import { NotificationContainer } from '../NotificationSystem';
import { useNotifications } from '../../hooks/useNotifications';

// Lazy loading de popups para mejor performance
const UserManagementPopup = lazy(() => import('./UserManagement/UserManagementPopup'));
const PatiosManagementPopup = lazy(() => import('./PatiosManagement/PatiosManagementPopup'));
const HistoryPopup = lazy(() => import('./HistoryPopup'));
const RoleManagementPopup = lazy(() => import('./RoleManagementPopup'));
const UnidadesNoMayoreadasPopup = lazy(() => import('./UnidadesNoMayoreadasPopup'));
const FacturadasNoEntregadasPopup = lazy(() => import('./FacturadasNoEntregadasPopup'));
const VendorsManagementPopup = lazy(() => import('./VendorsManagement/VendorsManagementPopup'));
const SimulateLecturePopup = lazy(() => import('./SimulateLecture'));

/**
 * Contenedor genérico para renderizar popups del Sidebar
 *
 * @component
 * @param {string} props.activePopup - Identificador del popup activo ('users', 'patios', etc.)
 * @param {Function} props.onClose - Callback para cerrar el popup activo
 * @param {string|null} props.facturadasInitialMarca - Marca inicial para el popup de facturadas
 */
const SidebarPopupContainer = ({ activePopup, onClose, facturadasInitialMarca }) => {
  const { vehiculosValidos } = useVehiculos();
  const { notifications, addNotification, removeNotification } = useNotifications();

  // Obtener vehículos no mayoreados si es necesario
  const vehiculosNoMayoreados = React.useMemo(() => {
    if (activePopup === 'unidadesNoMayoreadas') {
      return getVehiculosNoMayoreados(vehiculosValidos || []);
    }
    return [];
  }, [activePopup, vehiculosValidos]);

  return (
    <>
    <Suspense fallback={null}>
      {activePopup && (<>
      {activePopup === 'users' && (
        <UserManagementPopup
          isOpen={true}
          onClose={onClose}
        />
      )}

      {activePopup === 'patios' && (
        <PatiosManagementPopup
          isOpen={true}
          onClose={onClose}
        />
      )}

      {activePopup === 'roles' && (
        <RoleManagementPopup
          isOpen={true}
          onClose={onClose}
        />
      )}

      {activePopup === 'vendors' && (
        <VendorsManagementPopup
          isOpen={true}
          onClose={onClose}
        />
      )}

      {activePopup === 'lecture' && (
        <SimulateLecturePopup
          isOpen={true}
          onClose={onClose}
          onSuccess={(msg) => addNotification({ type: 'success', title: 'Lectura enviada', message: msg, duration: 4000 })}
        />
      )}

      {activePopup === 'unidadesNoMayoreadas' && (
        <UnidadesNoMayoreadasPopup
          vehiculos={vehiculosNoMayoreados}
          isOpen={true}
          onClose={onClose}
        />
      )}

      {activePopup === 'facturadas' && (
        <FacturadasNoEntregadasPopup
          vehiculos={vehiculosValidos}
          isOpen={true}
          onClose={onClose}
          initialMarca={facturadasInitialMarca}
        />
      )}

      {activePopup === 'history' && (
        <HistoryPopup
          isOpen={true}
          onClose={onClose}
        />
      )}
      </>)}
    </Suspense>
    <NotificationContainer notifications={notifications} onRemove={removeNotification} />
    </>
  );
};

export default React.memo(SidebarPopupContainer);
